import React from 'react';

import { CheckCircleOutlined, StopOutlined } from '@ant-design/icons';

import Badge from '@/components/Badge';
import TableRowButtons from '@/components/Common/TableRowButtons';
import { DEFAULT_EMPTY_VALUE } from '@/utils/constants';
import {
  createEnumOptions,
  getEnumTranslationKey,
  parseOptionalNumericEnumFromApi,
} from '@/utils/core/data-transformation/enumHelpers';
import { CoreEnums } from '@/utils/domain/constants';
import { createTableActionsColumn, formatTZLocal, getEnumNamespace } from '@/utils/infrastructure';
import { createHighlightRender } from '@/utils/infrastructure/highlightColumns';
import Filters, { registerModuleFilters } from '@/utils/input/filters';

import { MODEL_NAME, REGISTRATION_REVIEW_ACTION } from './constants';

const { BaseFilters } = Filters;

const requestStatusBadgeConfig = {
  [CoreEnums.ChannelRegistrationApprovalStatus.Pending]: {
    status: 'processing',
    type: 'process',
  },
  [CoreEnums.ChannelRegistrationApprovalStatus.Approved]: {
    status: 'approved',
    type: 'process',
  },
  [CoreEnums.ChannelRegistrationApprovalStatus.Rejected]: {
    status: 'rejected',
    type: 'process',
  },
};

const STATUS_ENUM_NAMESPACE = getEnumNamespace(
  CoreEnums.ChannelRegistrationApprovalStatus,
  'Channel.Onboarding',
);

const DEFAULT_PENDING_STATUS_FILTER = `status eq ${STATUS_ENUM_NAMESPACE}'Pending'`;

const REGISTRATION_QUEUE_ACTOR_KIND_VALUES = new Set([
  CoreEnums.ChannelActorKind.Operator,
  CoreEnums.ChannelActorKind.Distributor,
  CoreEnums.ChannelActorKind.Partner,
  CoreEnums.ChannelActorKind.Reseller,
  CoreEnums.ChannelActorKind.ReferralAgent,
]);

const StatusFilter = {
  ...BaseFilters.EnumFilter,
  name: 'status',
  label: 'global.text.status',
  enumNamespace: STATUS_ENUM_NAMESPACE,
  enumSource: CoreEnums.ChannelRegistrationApprovalStatus,
  defaultValue: CoreEnums.ChannelRegistrationApprovalStatus.Pending,
  options: createEnumOptions(
    CoreEnums.ChannelRegistrationApprovalStatus,
    'channelRegistrationApprovalStatus',
    { casing: 'camel' },
  ),
  badgeMap: requestStatusBadgeConfig,
  badgeType: 'process',
};

const ActorKindFilter = {
  ...BaseFilters.EnumFilter,
  name: 'actorKind',
  label: 'channelRegistrationApprovals.columns.actorKind',
  enumNamespace: getEnumNamespace(CoreEnums.ChannelActorKind, 'Channel.Actors'),
  enumSource: CoreEnums.ChannelActorKind,
  options: createEnumOptions(CoreEnums.ChannelActorKind, 'channelActorKind', {
    casing: 'camel',
  }).filter((option) => REGISTRATION_QUEUE_ACTOR_KIND_VALUES.has(option.value)),
};

const RequestedAtDateFilter = {
  ...Filters.DateFilter,
  name: 'requestedAt',
  label: 'channelRegistrationApprovals.columns.requestedAt',
  defaultValue: CoreEnums.FilterDateRange.Disabled,
};

export const filtersConfig = [StatusFilter, ActorKindFilter, RequestedAtDateFilter];

registerModuleFilters(MODEL_NAME, filtersConfig);

const stopRowClick = (e) => {
  e?.preventDefault?.();
  e?.stopPropagation?.();
};

const formatUserLabel = (user) => {
  if (!user || typeof user !== 'object') return '';
  const fullName = [user.names, user.lastNames].filter(Boolean).join(' ').trim();
  return user.email || user.fullName || fullName;
};

export const formatRequesterLabel = (record) => {
  const userLabel = formatUserLabel(record?.requestedByUser);
  if (userLabel) return userLabel;
  return record?.requestedByUserId || DEFAULT_EMPTY_VALUE;
};

const formatNestedCompanyName = (company) =>
  company?.name || company?.commercialName || company?.identity || company?.id || null;

export const formatCompanyLabel = (record) =>
  formatNestedCompanyName(record?.company) || DEFAULT_EMPTY_VALUE;

export const formatRegistrarLabel = (record) =>
  formatNestedCompanyName(record?.registrar) || DEFAULT_EMPTY_VALUE;

export const formatAgreementLabel = (record) => {
  const name = record?.agreement?.name;
  const version = record?.agreement?.versionNumber;
  if (name && version != null && version !== '') return `${name} v${version}`;
  if (name) return name;
  return record?.agreement?.id || record?.agreementId || DEFAULT_EMPTY_VALUE;
};

export const formatParentLabel = (record, formatMessage) => {
  const parent = record?.parent;
  if (!parent) {
    return formatMessage({ id: 'channelRegistrationApprovals.parent.none' });
  }
  return (
    parent.companyName ||
    formatNestedCompanyName(parent.company) ||
    parent.companyId ||
    DEFAULT_EMPTY_VALUE
  );
};

export const isPendingRegistrationApproval = (record) => {
  const status = parseOptionalNumericEnumFromApi(
    CoreEnums.ChannelRegistrationApprovalStatus,
    record?.status,
  );
  return status === CoreEnums.ChannelRegistrationApprovalStatus.Pending;
};

export const getRegistrationApprovalActionFlags = (record, currentUserId) => {
  const isPending = isPendingRegistrationApproval(record);
  const requesterId = record?.requestedByUserId || record?.requestedByUser?.id;
  const isSelf =
    Boolean(currentUserId) && Boolean(requesterId) && String(requesterId) === String(currentUserId);

  return {
    canApprove: isPending && !isSelf,
    canReject: isPending,
  };
};

export const applyDefaultPendingStatusFilter = (payload = {}) => {
  const filter = payload.filter;
  const filterText = filter == null ? '' : String(filter);
  if (filterText && /\bstatus\b/i.test(filterText)) {
    return payload;
  }
  return {
    ...payload,
    filter: filterText
      ? `(${filterText}) and (${DEFAULT_PENDING_STATUS_FILTER})`
      : DEFAULT_PENDING_STATUS_FILTER,
  };
};

const renderRequestStatusBadge = (formatMessage, value) => {
  const resolved = parseOptionalNumericEnumFromApi(
    CoreEnums.ChannelRegistrationApprovalStatus,
    value,
  );
  const config = resolved !== undefined ? requestStatusBadgeConfig[resolved] : null;
  if (!config) {
    return <Badge status="default" type="process" text={DEFAULT_EMPTY_VALUE} />;
  }
  return (
    <Badge
      status={config.status}
      type={config.type}
      text={formatMessage({
        id: getEnumTranslationKey(
          CoreEnums.ChannelRegistrationApprovalStatus,
          resolved,
          'channelRegistrationApprovalStatus',
        ),
      })}
    />
  );
};

const renderActorKindBadge = (formatMessage, value) => {
  const resolved = parseOptionalNumericEnumFromApi(CoreEnums.ChannelActorKind, value);
  if (resolved === undefined) {
    return <Badge status="default" type="group-item" text={DEFAULT_EMPTY_VALUE} />;
  }
  return (
    <Badge
      status="default"
      type="group-item"
      text={formatMessage({
        id: getEnumTranslationKey(CoreEnums.ChannelActorKind, resolved, 'channelActorKind'),
      })}
    />
  );
};

const mapEnumDetailRow = (row, enumSource, raw, translationPrefix, badge) => {
  const resolved = parseOptionalNumericEnumFromApi(enumSource, raw);
  if (resolved === undefined) {
    return { ...row, value: DEFAULT_EMPTY_VALUE };
  }
  return {
    ...row,
    value: getEnumTranslationKey(enumSource, resolved, translationPrefix),
    locale: true,
    ...(badge || {}),
  };
};

export function mapChannelRegistrationApprovalDetailFields(rows, { item, formatMessage }) {
  if (!item || !rows?.length) return rows;

  return rows
    .map((row) => {
      if (row.name === 'status') {
        const resolved = parseOptionalNumericEnumFromApi(
          CoreEnums.ChannelRegistrationApprovalStatus,
          item.status,
        );
        const config = resolved !== undefined ? requestStatusBadgeConfig[resolved] : null;
        return mapEnumDetailRow(
          row,
          CoreEnums.ChannelRegistrationApprovalStatus,
          item.status,
          'channelRegistrationApprovalStatus',
          {
            badgeStatus: config?.status || 'default',
            badgeType: config?.type || 'process',
          },
        );
      }

      if (row.name === 'actorKind') {
        return mapEnumDetailRow(
          row,
          CoreEnums.ChannelActorKind,
          item.actorKind,
          'channelActorKind',
          { badgeStatus: 'default', badgeType: 'group-item' },
        );
      }

      if (row.name === 'company.name') {
        return { ...row, value: formatCompanyLabel(item) };
      }

      if (row.name === 'company.identity') {
        return { ...row, value: item.company?.identity || DEFAULT_EMPTY_VALUE };
      }

      if (row.name === 'registrar.name') {
        return { ...row, value: formatRegistrarLabel(item) };
      }

      if (row.name === 'parent') {
        return {
          ...row,
          value: formatMessage
            ? formatParentLabel(item, formatMessage)
            : item.parent?.companyName || DEFAULT_EMPTY_VALUE,
        };
      }

      if (row.name === 'parent.actorKind') {
        if (!item.parent) return null;
        return mapEnumDetailRow(
          row,
          CoreEnums.ChannelActorKind,
          item.parent.actorKind,
          'channelActorKind',
          { badgeStatus: 'default', badgeType: 'group-item' },
        );
      }

      if (row.name === 'agreement') {
        return { ...row, value: formatAgreementLabel(item) };
      }

      if (row.name === 'requester') {
        return { ...row, value: formatRequesterLabel(item) };
      }

      if (row.name === 'approvalRequired') {
        return {
          ...row,
          value: item.approvalRequired ? 'global.text.yes' : 'global.text.no',
          locale: true,
        };
      }

      if (row.name === 'registrationApprovalPolicy') {
        return mapEnumDetailRow(
          row,
          CoreEnums.ChannelRegistrationApprovalPolicy,
          item.registrationApprovalPolicy,
          'channelAgreement.registrationApprovalPolicy',
        );
      }

      if (row.name === 'rejectionReason') {
        const reason = item.rejectionReason;
        if (reason == null || String(reason).trim() === '') return null;
        return { ...row, value: reason };
      }

      return row;
    })
    .filter(Boolean);
}

const buildReviewMenuItems = (formatMessage, record, flags, openReview) => {
  const items = [];
  if (flags.canApprove) {
    items.push({
      key: 'approve',
      label: formatMessage({ id: 'global.button.approve' }),
      icon: <CheckCircleOutlined />,
      onClick: (e) => {
        stopRowClick(e);
        openReview?.(record.id, REGISTRATION_REVIEW_ACTION.APPROVE);
      },
    });
  }
  if (flags.canReject) {
    items.push({
      key: 'reject',
      label: formatMessage({ id: 'global.button.reject' }),
      icon: <StopOutlined />,
      danger: true,
      onClick: (e) => {
        stopRowClick(e);
        openReview?.(record.id, REGISTRATION_REVIEW_ACTION.REJECT);
      },
    });
  }
  if (items.length) {
    items.push({ key: 'divider-review', type: 'divider' });
  }
  return items;
};

export function buildChannelRegistrationApprovalColumns({
  formatMessage,
  openDrawerCreateOrEdit,
  searchCriteria,
  currentUserId,
  openReview,
}) {
  const highlight = createHighlightRender(searchCriteria);

  return [
    {
      key: 'requestedAt',
      title: formatMessage({ id: 'channelRegistrationApprovals.columns.requestedAt' }),
      dataIndex: 'requestedAt',
      sorter: true,
      width: 180,
      render: (value) => (value ? formatTZLocal(value) : DEFAULT_EMPTY_VALUE),
    },
    {
      key: 'status',
      title: formatMessage({ id: 'global.text.status' }),
      dataIndex: 'status',
      align: 'center',
      sorter: true,
      render: (value) => renderRequestStatusBadge(formatMessage, value),
    },
    {
      key: 'requester',
      title: formatMessage({ id: 'global.text.applicant' }),
      dataIndex: 'requestedByUserId',
      render: (_, record) => highlight(formatRequesterLabel(record)),
    },
    {
      key: 'actorKind',
      title: formatMessage({ id: 'channelRegistrationApprovals.columns.actorKind' }),
      dataIndex: 'actorKind',
      align: 'center',
      sorter: true,
      render: (value) => renderActorKindBadge(formatMessage, value),
    },
    {
      key: 'company',
      title: formatMessage({ id: 'channelRegistrationApprovals.columns.company' }),
      dataIndex: ['company', 'name'],
      render: (_, record) => highlight(formatCompanyLabel(record)),
    },
    {
      key: 'registrar',
      title: formatMessage({ id: 'channelRegistrationApprovals.columns.registrar' }),
      dataIndex: ['registrar', 'name'],
      render: (_, record) => highlight(formatRegistrarLabel(record)),
    },
    {
      key: 'parent',
      title: formatMessage({ id: 'channelRegistrationApprovals.columns.parent' }),
      dataIndex: ['parent', 'companyName'],
      render: (_, record) => formatParentLabel(record, formatMessage),
    },
    {
      key: 'agreement',
      title: formatMessage({ id: 'global.text.agreement' }),
      dataIndex: ['agreement', 'name'],
      render: (_, record) => highlight(formatAgreementLabel(record)),
    },
    createTableActionsColumn({
      alwaysDropdown: true,
      inlineActionCount: 1,
      render: (_, record) => {
        const flags = getRegistrationApprovalActionFlags(record, currentUserId);
        return (
          <TableRowButtons
            onDetail={() => openDrawerCreateOrEdit(record.id, true)}
            formatMessage={formatMessage}
            forceDropdown
            customItems={buildReviewMenuItems(formatMessage, record, flags, openReview)}
            actionOrder={{
              primary: [],
              secondary: ['approve', 'reject', 'divider-review', 'detail'],
            }}
          />
        );
      },
    }),
  ];
}
